/**
 * Created by rathompson on 5/9/22
 * File: AddDeviceModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Banner,
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  Bold,
  Select,
  noop,
  LoadingIcon,
  copy
} from '@panwds/react-ui'
import _ from 'lodash'
import { BUTTON_CANCEL, BUTTON_SAVE, LABEL_BANNER_XDR_WARNING, LABEL_CSP, TITLE_ADD_DEVICE, TOOLTIP_ADD_DEVICE_XDR_WARNING, BUTTON_COPY_XDR_SERIAL_NUMBERS } from '../../constants/LabelConstants'
import CSPDeviceTable from './CSPDeviceTable'
import styled from 'styled-components'
import { ARRAY, getTSGInstancesBasedSupportAccount } from '../../../common/utils/Utils'
import { LoadCSPDevices } from '../../../common/actions/GlobalActions'
import SubscriptionService from '../../../subscription/services/SubscriptionService'
import AuthContext from '../../../common/context/AuthContext'
import { APP_ID_LOGGING_SERVICE } from '../../../common/constants/GlobalConstants'
import AppBanner from '../../../subscription/components/EnterpriseActivation/components/Licenses/AppBanner'
import { APP_ID_AUTO_CORTEX_XDR, APP_ID_CORTEX_XDR } from '../../../activation/components/common/GlobalConstants'

const CONTAINER = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1;

  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }

  button[id^=table] {
    color: #333333;
  }

  button[aria-label="Previous"] {
    color: #333333;
  }

  button[aria-label="Next"] {
    color: #333333;
  }

  .tw-flex.tw-bg-white {
    flex: 1 1;
  }
`
const bannerProps = {
  warning: <span>The Customer Support Account for this device tenant group has been chosen based on the activated license products.<br/>To associate VMFlex devices to a Tenant, use the  Deployment profile in the Customer Support Portal.</span>,
  className: 'tw-px-0 tw-py-0'
}

const AddDeviceModal = ({
  isOpen,
  closeModal,
  tenant = {},
  dispatch = noop,
  fetchDevices = async () => [],
  callback = (data) => {
  },
  instances = [],
}) => {

  const [loading, setLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [devices, setDevices] = useState([])
  const [deviceCache, setDeviceCache] = useState({})
  const [selected, setSelected] = useState([])
  const [xdrSerialNumbers, setXdrSerialNumbers] = useState([])
  const { user } = useContext(AuthContext)

  const ref = useRef(tenant)

  const userAccounts = useMemo(() => user?.supportAccountIds || [], [user])
  const TsgInstancesSupportAccount = useMemo(() => {
    try {
      const supportAccountId = getTSGInstancesBasedSupportAccount({ tsgInstances: instances, supportAccounts: userAccounts, allowFederal: true })
      return supportAccountId?.[0] || {}
    }
    catch (error) {
      setError(error.message || 'Failed to find TSG instances support account')
      setLoading(false)
      return {}
    }
  }, [instances, userAccounts])

  const getSupportAccountSignature = useCallback((support_account_id) => {
    return userAccounts.find(e => e.accountid === support_account_id)?.signature
  }, [userAccounts])

  const tenantRegion = useMemo(() => instances?.[0]?.region || '', [instances])

  const fetchData = useCallback(async (tsg_id, support_account_id, dispatch) => {
    setLoading(true)
    setDevices([])
    const notonorafter = user?.notonorafter
    const signature = getSupportAccountSignature(support_account_id)
    const x_sig = encodeURIComponent(`${support_account_id} ${notonorafter} ${signature}`)
    try {
      const allDevices = ARRAY.hasArray(deviceCache[support_account_id]) ? deviceCache[support_account_id] :
        await LoadCSPDevices(support_account_id, x_sig, dispatch)
      //Devices that are not associated with TSG or CDL
      const tsgCdlTenantId = instances.find(instance => instance.app_id === APP_ID_LOGGING_SERVICE)?.tenant_id
      const devices = _.orderBy(allDevices.filter(e => {
        e.disabled = false
        e.hasWarning = false
        e.warningMessage = ''
        // paid CDL + log persist (INGEST_AND_STORE) + not matching TSG CDL
        // CLCS -> log persist will be INGEST and we only check whether device is already in tsg or not, cdl_tenant_id to TSG-cdl mapping ignored
        if (e.cdl_tenant_id && e.subscription_type === 'INGEST_AND_STORE' && e.cdl_tenant_id !== tsgCdlTenantId) { // new CDL-device mapping for fw >10.0
          e.disabled = true
          e.reason = `device is already associated with different cdl tenant id ${e.cdl_tenant_id}`
        }
        else if (e.platform_id && e.platform_id !== tsgCdlTenantId) { // old IT-cdl mapping for FW <10.0
          e.disabled = true
          e.reason = `device is already associated with different cdl platform id ${e.platform_id}`
        }
        // IoT customer tenants are moved to TSG hence commenting below code
        // // till all IOT work-flows moves to TSG, we need to do special check for devices i.e; check device not mapped to any non-TSG IoT (especially required for INGEST clcs-devices) or different TSG IoT
        // else if (e.iot && (!e.iot.tsg_id || e.iot?.iot_tenant_id !== tsgIoTTenantId)) {
        //   e.disabled = true
        //   e.reason = `device is already associated with different IoT tenant ${e.iot?.iot_tenant_id}`
        // }

        // //return !e.tsg_id && (!cdlTenantId || cdlTenantId === tsgCdlTenantId)

        // If device has CDL with CORTEX_XDR, check to make sure it is mapped to Tenant's Region
        if (ARRAY.hasArray(e.cdl?.application_ids) && (e.cdl?.application_ids.includes(APP_ID_CORTEX_XDR) || e.cdl?.application_ids.includes(APP_ID_AUTO_CORTEX_XDR))) {
          const hasXDRMismatch = tenantRegion !== e.cdl?.region
          if (hasXDRMismatch) {
            e.hasXDRMismatch = true
            e.hasWarning = hasXDRMismatch
            e.warningMessage = hasXDRMismatch ? TOOLTIP_ADD_DEVICE_XDR_WARNING.replace('{tenantRegion}', tenantRegion) : ''
          }
        }

        return !e.tsg_id
      }), ['disabled'], ['asc'])

      if (ref.current?.id === tsg_id) {
        setDevices(devices)
        deviceCache[support_account_id] = devices
        setDeviceCache(deviceCache)
        setLoading(false)
      }
    }
    catch (error) {
      setError(error.message)
      setLoading(false)
    }
  }, [user?.notonorafter, getSupportAccountSignature, deviceCache, instances])

  useEffect(() => {
    ref.current = tenant
    if (TsgInstancesSupportAccount?.id) {
      fetchData(tenant?.id, TsgInstancesSupportAccount.id, dispatch)
    }
  }, [TsgInstancesSupportAccount, tenant, dispatch, fetchData])

  const onSave = useCallback(async () => {
    try {
      setIsLoading(true)
      const devices = selected.map(({ original }) => ({ serial_number: original.serial_number }))
      const { id: support_account_id } = TsgInstancesSupportAccount
      const signature = getSupportAccountSignature(support_account_id)
      const notonorafter = user?.notonorafter
      const x_sig = `${support_account_id} ${notonorafter} ${signature}`
      const tsg_id = tenant?.id
      const { data } = await SubscriptionService.devices.associateDevices({
        tsg_id,
        support_account_id: String(support_account_id),
        devices,
        x_sig
      })
      await fetchDevices()
      closeModal()
      callback(data)
    }
    catch (error) {
      setError(error.message)
    }
    setIsLoading(false)
  }, [selected, TsgInstancesSupportAccount, getSupportAccountSignature, user?.notonorafter, tenant?.id, fetchDevices, closeModal, callback])

  const showXDRBanner = useMemo(() => {
    const xdrMismatchSelected = selected.filter(({ original }) => original.hasXDRMismatch)
    if (xdrMismatchSelected.length) {
      setXdrSerialNumbers(xdrMismatchSelected.map(({ original }) => original.serial_number))
      return true
    }
    return false
  }, [selected, setXdrSerialNumbers])

  const copyData = useCallback(() => {
    copy(JSON.stringify(xdrSerialNumbers))
  }, [xdrSerialNumbers])

  const renderXDRWarningBanner = () => {
    const message = LABEL_BANNER_XDR_WARNING.replaceAll('{tenantRegion}', tenantRegion)

    return (
      <div className="tw-py-2">
        <Banner showIcon appearance='warning' actions={
          <>
            <Button onClick={copyData}>{BUTTON_COPY_XDR_SERIAL_NUMBERS}</Button>
          </>
        }>
          <span>{message}</span>
        </Banner>
      </div>
    )
  }

  return (
    <Modal
      style={{ top: '50%', '--tw-translate-y': '-50%', minHeight: '800px', maxHeight: '800px' }}
      size="xl"
      onClose={closeModal}
      {...{ isOpen }}
    >
      <ModalHeader
        title={TITLE_ADD_DEVICE}
      />
      <div className="tw-relative tw-flex tw-flex-col tw-w-full tw-h-full tw-p-4 tw-box-border tw-font-lato tw-flex-1">
        {isLoading && (
          <div
            className="tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center tw-z-50 tw-bg-gray tw-opacity-60">
            <LoadingIcon size="4xl" />
          </div>
        )}
        {Boolean(error) && <Bold appearance="semibold" addClassName="tw-text-error">{error}</Bold>}
        <div className="tw-flex tw-flex-col tw-mb-2" style={{ width: '304px' }}>
          <Bold appearance="semibold">{LABEL_CSP}</Bold>
          <Select
            addClassName='cmm-modal-select'
            items={[TsgInstancesSupportAccount]}
            selectedItem={TsgInstancesSupportAccount}
            disabled={true}
            button={(
              <Button
                addClassName="tw-justify-between tw-mt-2"
                isMenu
                fullWidth
                appearance="tertiary"
              >
                {TsgInstancesSupportAccount?.value}
              </Button>
            )}
          />
        </div>
        <AppBanner { ...bannerProps } ></AppBanner>
        {showXDRBanner && renderXDRWarningBanner()}
        <CONTAINER>
          <CSPDeviceTable setSelected={setSelected} data={devices} loading={loading} />
        </CONTAINER>
      </div>
      <ModalFooter>
        <Button
          appearance="secondary"
          disabled={loading || isLoading}
          onClick={closeModal}>
          {BUTTON_CANCEL}
        </Button>
        <Button
          appearance="primary"
          disabled={!ARRAY.hasArray(selected) || loading || isLoading}
          onClick={onSave}>
          {BUTTON_SAVE}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

AddDeviceModal.propTypes = {}
export default AddDeviceModal
